import React from "react";
import { Link } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Button,
  Stack,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PlaceIcon from "@mui/icons-material/Place";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { fDateTime } from "../../utils";
import ImageWithFallback from "../ImageWithFallback";

const SectionBox = styled(Box)({
  padding: "0.5rem 0 4rem 0",
  backgroundColor: "#f8fafc",
});

const SectionSubtitle = styled(Typography)({
  textAlign: "center",
  color: "#666",
  marginBottom: "2.5rem",
});

const StyledCard = styled(Card)({
  height: "100%",
  display: "flex",
  flexDirection: "column",
  borderRadius: "12px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
  transition: "transform 0.2s, box-shadow 0.2s",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: "0 8px 30px rgba(0,0,0,0.1)",
  },
});

const StyledCardMedia = styled(CardMedia)({
  height: 420,
  objectFit: "cover",
});

const getFileType = (url) => url?.split(".").pop()?.toLowerCase();

const stripHtml = (html) => {
  if (!html) return "";
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
};

const HomeAnnouncements = ({ announcements = [] }) => {
  const latest = Array.isArray(announcements) ? announcements.slice(0, 3) : [];

  if (latest.length === 0) {
    return (
      <SectionBox id="announcements" component="section">
        <Container maxWidth="lg">
          <Typography
            variant="h4"
            fontWeight="bold"
            textAlign="center"
            gutterBottom
            color="rgba(26,128,200)"
            sx={{ mt: 4, mb: 1 }}
          >
            Announcements
          </Typography>

          <SectionSubtitle>Latest updates and events</SectionSubtitle>

          <Typography textAlign="center" color="#666">
            No announcements are available at this time.
          </Typography>

          <Box sx={{ textAlign: "center", mt: 4 }}>
            <Button
              component={Link}
              to="/announcements"
              variant="outlined"
              color="primary"
              endIcon={<ArrowForwardIcon />}
              size="large"
            >
              View all announcements
            </Button>
          </Box>
        </Container>
      </SectionBox>
    );
  }

  return (
    <SectionBox id="announcements" component="section">
      <Container maxWidth="lg">
        <Typography
          variant="h4"
          fontWeight="bold"
          textAlign="center"
          gutterBottom
          color="rgba(26,128,200)"
          sx={{ mt: 4, mb: 1 }}
        >
          Announcements
        </Typography>
        <SectionSubtitle>Latest updates and events</SectionSubtitle>

        <Grid container spacing={3}>
          {latest.map((item) => (
            <Grid item xs={12} sm={6} md={4} key={item._id}>
              <StyledCard>
                <CardActionArea component={Link} to={`/announcements/${item._id}`}>
                  <StyledCardMedia>
                    {getFileType(item?.url) === "pdf" ? (
                      <img
                        src="/pdf.png"
                        alt={item.title}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                    ) : (
                      <ImageWithFallback
                        path={item?.url}
                        alt={item.title}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                    )}
                  </StyledCardMedia>
                  <CardContent sx={{ flexGrow: 1, textAlign: "left" }}>
                    <Typography variant="h6" fontWeight={600} gutterBottom noWrap>
                      {item.title}
                    </Typography>
                    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color: "#666", fontSize: "0.875rem" }}>
                      <CalendarTodayIcon sx={{ fontSize: 16 }} />
                      <span>{fDateTime(item?.date)}</span>
                    </Stack>
                    {item?.location && (
                      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color: "#666", fontSize: "0.875rem", mt: 0.5 }}>
                        <PlaceIcon sx={{ fontSize: 16 }} />
                        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.location}</span>
                      </Stack>
                    )}
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1, display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}
                    >
                      {(() => {
                        const t = stripHtml(item?.description).slice(0, 180);
                        return t.length >= 180 ? `${t}...` : t || "View details";
                      })()}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </StyledCard>
            </Grid>
          ))}
        </Grid>

        <Box sx={{ textAlign: "center", mt: 4 }}>
          <Button
            component={Link}
            to="/announcements"
            variant="outlined"
            color="primary"
            endIcon={<ArrowForwardIcon />}
            size="large"
          >
            View all announcements
          </Button>
        </Box>
      </Container>
    </SectionBox>
  );
};

export default HomeAnnouncements;
